<template>
    <v-container fluid class="pa-4">
        <div class="d-flex align-center mb-4">
            <v-icon color="primary" size="28" class="mr-3">mdi-chart-areaspline</v-icon>
            <div>
                <h1 class="text-h5 font-weight-bold">Executive Dashboard</h1>
                <p class="text-caption text-medium-emphasis mb-0">Business performance at a glance</p>
            </div>
            <v-spacer/>
            <v-btn variant="text" :loading="loading" @click="loadData" prepend-icon="mdi-refresh">Refresh</v-btn>
        </div>

        <v-overlay :model-value="loading && !data" contained class="align-center justify-center">
            <v-progress-circular indeterminate size="48"/>
        </v-overlay>

        <v-alert v-if="error" type="error" variant="tonal" closable class="mb-4" @click:close="error = null">
            {{ error }}
            <template v-slot:append>
                <v-btn variant="text" size="small" @click="loadData">Retry</v-btn>
            </template>
        </v-alert>

        <template v-if="data">
            <!-- KPI Cards Row 1: Revenue -->
            <v-row dense class="mb-2">
                <v-col cols="12" md="3">
                    <KpiCard title="Monthly Recurring Revenue" :value="formatCurrency(data.revenue.mrr)"
                             icon="mdi-currency-usd" color="green"
                             :trend="data.revenue.mrr_growth" trend-label="vs last month"/>
                </v-col>
                <v-col cols="12" md="3">
                    <KpiCard title="Annual Recurring Revenue" :value="formatCurrency(data.revenue.arr)"
                             icon="mdi-chart-timeline-variant" color="blue"/>
                </v-col>
                <v-col cols="12" md="3">
                    <KpiCard title="Avg Revenue Per Company" :value="formatCurrency(data.revenue.arpu)"
                             icon="mdi-account-cash" color="purple"/>
                </v-col>
                <v-col cols="12" md="3">
                    <KpiCard title="Unpaid Invoices" :value="data.revenue.unpaid_invoices"
                             :subtitle="formatCurrency(data.revenue.unpaid_amount) + ' outstanding'"
                             icon="mdi-alert-circle" :color="data.revenue.unpaid_invoices > 0 ? 'red' : 'green'"/>
                </v-col>
            </v-row>

            <!-- KPI Cards Row 2: Companies & Devices -->
            <v-row dense class="mb-2">
                <v-col cols="12" md="3">
                    <KpiCard title="Active Companies" :value="data.companies.total_active"
                             :subtitle="'+' + data.companies.new_this_month + ' this month'"
                             icon="mdi-domain" color="blue"
                             :trend="data.companies.growth" trend-label="vs last month"/>
                </v-col>
                <v-col cols="12" md="3">
                    <KpiCard title="Active Devices" :value="data.devices.total_active"
                             :subtitle="'+' + data.devices.new_this_month + ' this month'"
                             icon="mdi-router-wireless" color="teal"
                             :trend="data.devices.growth" trend-label="vs last month"/>
                </v-col>
                <v-col cols="12" md="3">
                    <KpiCard title="Avg Devices / Company" :value="data.devices.avg_per_company"
                             icon="mdi-chart-bar" color="indigo"/>
                </v-col>
                <v-col cols="12" md="3">
                    <KpiCard title="Pending Approval" :value="data.companies.pending_approval"
                             icon="mdi-clock-outline"
                             :color="data.companies.pending_approval > 0 ? 'orange' : 'green'"
                             :clickable="data.companies.pending_approval > 0"
                             @click="data.companies.pending_approval > 0 && $router.push('/pending-companies')"/>
                </v-col>
            </v-row>

            <!-- KPI Cards Row 3: Users, Inventory, Payments -->
            <v-row dense class="mb-4">
                <v-col cols="12" md="3">
                    <KpiCard title="Total Users" :value="data.users.total"
                             :subtitle="'+' + data.users.new_this_month + ' this month'"
                             icon="mdi-account-group" color="purple"/>
                </v-col>
                <v-col cols="12" md="3">
                    <KpiCard title="Devices in Inventory" :value="data.devices.inventory"
                             icon="mdi-package-variant" color="grey"/>
                </v-col>
                <v-col cols="12" md="3">
                    <KpiCard title="Expiring Subscriptions" :value="data.devices.expiring_subscriptions"
                             subtitle="Within 30 days"
                             icon="mdi-calendar-alert"
                             :color="data.devices.expiring_subscriptions > 5 ? 'orange' : 'green'"/>
                </v-col>
                <v-col cols="12" md="3">
                    <KpiCard title="Payment Failures" :value="data.payments.failures_this_month"
                             subtitle="This month"
                             icon="mdi-credit-card-off"
                             :color="data.payments.failures_this_month > 0 ? 'red' : 'green'"/>
                </v-col>
            </v-row>

            <!-- Quick Navigation -->
            <v-row dense>
                <v-col cols="12" md="4">
                    <v-card variant="outlined" class="pa-4 cursor-pointer report-link" @click="$router.push('/revenue-report')">
                        <div class="d-flex align-center">
                            <v-icon color="green" size="36" class="mr-3">mdi-cash-multiple</v-icon>
                            <div>
                                <div class="text-subtitle-1 font-weight-bold">Revenue & Billing</div>
                                <div class="text-caption text-medium-emphasis">MRR trends, plan breakdown, aging, discounts</div>
                            </div>
                            <v-spacer/>
                            <v-icon>mdi-chevron-right</v-icon>
                        </div>
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <v-card variant="outlined" class="pa-4 cursor-pointer report-link" @click="$router.push('/growth-report')">
                        <div class="d-flex align-center">
                            <v-icon color="blue" size="36" class="mr-3">mdi-trending-up</v-icon>
                            <div>
                                <div class="text-subtitle-1 font-weight-bold">Growth & Signups</div>
                                <div class="text-caption text-medium-emphasis">Company signups, activations, geography</div>
                            </div>
                            <v-spacer/>
                            <v-icon>mdi-chevron-right</v-icon>
                        </div>
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <v-card variant="outlined" class="pa-4 cursor-pointer report-link" @click="$router.push('/operations-report')">
                        <div class="d-flex align-center">
                            <v-icon color="orange" size="36" class="mr-3">mdi-cog-outline</v-icon>
                            <div>
                                <div class="text-subtitle-1 font-weight-bold">Operations & Health</div>
                                <div class="text-caption text-medium-emphasis">Payment health, device status, subscriptions</div>
                            </div>
                            <v-spacer/>
                            <v-icon>mdi-chevron-right</v-icon>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Total Revenue footer -->
            <v-row dense class="mt-2">
                <v-col cols="12">
                    <v-card variant="tonal" color="primary" class="pa-3">
                        <div class="d-flex align-center justify-center">
                            <v-icon class="mr-2">mdi-bank</v-icon>
                            <span class="text-subtitle-1">Total Lifetime Revenue: <strong>{{ formatCurrency(data.revenue.total_revenue) }}</strong></span>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script setup>
import {ref, onMounted} from 'vue';
import {send} from '@/composables/apiClient';
import KpiCard from './components/KpiCard.vue';

const loading = ref(false);
const data = ref(null);
const error = ref(null);

function formatCurrency(value) {
    if (value == null) return '$0.00';
    return '$' + Number(value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

async function loadData() {
    loading.value = true;
    error.value = null;
    try {
        const response = await send('get', 'admin/reports/summary');
        if (response?.data?.data) {
            data.value = response.data.data;
        } else {
            error.value = response?.data?.message || 'Failed to load data';
        }
    } catch (e) {
        console.error('Failed to load summary', e);
        error.value = e?.response?.data?.message || e?.message || 'Failed to load data';
    } finally {
        loading.value = false;
    }
}

onMounted(loadData);
</script>

<style scoped>
.report-link:hover {
    border-color: rgb(var(--v-theme-primary)) !important;
    background: rgba(var(--v-theme-primary), 0.04);
}
</style>
